import React from 'react'
import { ColorFormat, Color as ColorType } from 'modules/shares/types'
import copy from './copy'
import { capitalize } from '@sketch/utils'
import {
  Attribute,
  AttributeLabel,
  AttributeList,
  FullCopyAttribute,
  Header,
  SubTitle,
  FillImage,
  GradientAttribute,
  Color,
} from '../../../components'
import { getDirtyAttributes } from '../../../components/Style/DirtyIconTooltip'
import {
  Fill,
  FillType,
  Gradient,
} from '../../../../../hooks/inspector/inspectorDataType/Styles'
import {
  InspectorLayersTreeRegularLayerNode,
  isGroupElementType,
} from '../../../../../hooks/inspector/sketchSceneLib'

export interface FillsProps {
  fills: Fill[]
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  layerType?: InspectorLayersTreeRegularLayerNode['type']
  originalValues?: Fill[]
}

function getDirtyHeaderAttributes(originalValues?: Fill[]) {
  if (!originalValues) {
    return undefined
  }

  // this indicates that previously the layer style did NOT have fills, but now it does
  if (
    originalValues[0] === null ||
    Object.values(originalValues[0]).length === 0
  ) {
    return { originalValue: 'disabled', originalProperty: 'Fill value' }
  }

  return undefined
}

const isBlendModeVisible = (blendMode: string) =>
  blendMode.toUpperCase() !== 'NORMAL'

const renderFill = (
  {
    isEnabled,
    type,
    color,
    gradient,
    patternFillType,
    patternTileScale,
    appearance,
  }: Fill,
  {
    onColorFormatChange,
    colorFormat,
  }: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  },
  i: number,
  originalValue?: Fill
) => {
  if (!isEnabled) {
    return null
  }

  const blendMode = appearance?.blendMode
  const opacity = appearance?.opacity ?? 1

  let renderedType = null
  switch (type) {
    case FillType.Color: {
      renderedType = color ? (
        <Color
          {...color}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          dirtyAttributes={
            originalValue?.color
              ? {
                  originalValue: originalValue?.color as ColorType,
                  originalProperty: 'Color',
                }
              : undefined
          }
        />
      ) : null
      break
    }
    case FillType.Gradient: {
      renderedType = gradient ? (
        <GradientAttribute
          gradient={gradient}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          opacity={opacity}
          dirtyAttributes={
            originalValue?.gradient
              ? {
                  originalValue: originalValue?.gradient as Gradient,
                  originalProperty: 'Gradient',
                }
              : undefined
          }
        />
      ) : null
      break
    }
    case FillType.Pattern: {
      renderedType = patternFillType ? (
        <Attribute display="inline">
          <AttributeLabel>Image</AttributeLabel>
          <FillImage
            opacity={opacity}
            fill={patternFillType}
            sizePercentage={patternTileScale}
          />
        </Attribute>
      ) : null
      break
    }
    default:
      renderedType = null
  }

  return (
    <AttributeList key={`Fill${i}`} data-testid="fill">
      {renderedType}
      {isBlendModeVisible(blendMode) && (
        <FullCopyAttribute
          label="Blend Mode"
          value={capitalize(blendMode)}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'blendMode',
            valueToDisplay: originalValue?.appearance.blendMode,
            originalValues: originalValue,
            labelToDisplay: 'Blend Mode',
          })}
        />
      )}
    </AttributeList>
  )
}

export const Fills: React.FC<FillsProps> = ({
  fills,
  colorFormat,
  onColorFormatChange,
  layerType,
  originalValues,
}) => {
  // TODO: move this processing to the `useGetInspectorData` once https://github.com/sketch-hq/Cloud/issues/3052 is done
  const reversedFills = [...fills].reverse()
  const reversedOriginalValues = originalValues
    ? [...originalValues].reverse()
    : undefined

  const renderedFills = reversedFills
    .map((fill, index) =>
      renderFill(
        fill,
        { colorFormat, onColorFormatChange },
        index,
        reversedOriginalValues ? reversedOriginalValues[index] : undefined
      )
    )
    .filter(element => element !== null)

  const copySectionValue = copy(reversedFills, colorFormat, layerType)

  const dirtyHeaderAttributes = getDirtyHeaderAttributes(originalValues)

  return renderedFills.length > 0 ? (
    <>
      <Header
        copyValue={copySectionValue}
        dirtyAttributes={dirtyHeaderAttributes}
      >
        <SubTitle>
          {layerType && isGroupElementType(layerType) ? 'Tints' : 'Fills'}
        </SubTitle>
      </Header>
      {renderedFills}
    </>
  ) : null
}
